<div class="flex border-b-2 border-primary">
  <div class="flex-1 p-2">
    <h1 class="mb-2 text-2xl font-bold tracking-tight text-gray-900 text-center">Manage Locations</h1>
    <p class="font-normal text-gray-700 text-center">Search by location to manage all associated users</p>
  </div>

  @if (selectedLocation(); as loc) {
    <div class="flex-1 flex items-center pl-2 italic">{{ loc.name }}</div>
  }
</div>

<div class="flex h-full">
  <div class="flex-1 flex flex-col h-[600px] border-r-2 border-b-2 border-primary">
    <div class="flex mx-[-1px]">
      <input
        panInput
        color="primary"
        class="w-full !rounded-none mt-[-1px]"
        placeholder="Filter {{ visibleLocations().length }} locations..."
        [(ngModel)]="locationFilter"
      />
    </div>

    <div class="flex-1 overflow-hidden overflow-y-auto">
      @if (locations().length === 0 && !hasLoadedUserLocations()) {
        <app-pan-blank-slate> <app-pan-spinner></app-pan-spinner> &nbsp;Loading... </app-pan-blank-slate>
      } @else if (locations().length === 0 && hasLoadedUserLocations()) {
        <app-pan-blank-slate>You do not have access to any locations.</app-pan-blank-slate>
      } @else if (visibleLocations().length === 0 && hasLoadedUserLocations()) {
        <app-pan-blank-slate>No locations match your query.</app-pan-blank-slate>
      } @else {
        <cdk-virtual-scroll-viewport itemSize="50" class="h-full">
          <div
            *cdkVirtualFor="let item of visibleLocations()"
            class="h-[50px] p-3 flex flex-col justify-center border-b-2 cursor-pointer"
            [class.bg-primary-no-hover]="item === selectedLocation()"
            (click)="selectLocation(item)"
          >
            <div
              #locationName
              #locationTooltip="matTooltip"
              class="truncate w-0 min-w-full shrink-0"
              [matTooltip]="item.name"
              (mouseenter)="locationTooltip.disabled = locationName.scrollWidth <= locationName.clientWidth"
            >
              {{ item.name }}
            </div>

            <div class="text-sm italic">
              {{ item.branch_name }}
            </div>
          </div>
        </cdk-virtual-scroll-viewport>
      }
    </div>
  </div>

  <div class="flex-1 h-[600px] overflow-hidden overflow-y-auto border-r-2 border-b-2 border-primary">
    @if (visibleUsers().length > 0) {
      <div class="flex mx-[-1px] items-center relative">
        <input
          panInput
          color="primary"
          class="w-full !rounded-none mt-[-1px]"
          placeholder="Filter {{ visibleUsers().length }} users..."
          [(ngModel)]="userFilter"
        />

        <app-pan-button data-testid="add-user-button" class="absolute right-[10px]" size="sm" color="info" (click)="addUserModal.show()">Add User</app-pan-button>
      </div>
    }

    @if (!selectedLocation()) {
      <app-pan-blank-slate>Select a location to see users for it.</app-pan-blank-slate>
    } @else if (selectedLocation() && !hasLoadedLocationUsers()) {
      <app-pan-blank-slate> <app-pan-spinner></app-pan-spinner> &nbsp;Loading... </app-pan-blank-slate>
    } @else if (selectedLocation() && hasLoadedLocationUsers() && visibleUsers().length === 0) {
      <app-pan-blank-slate>
        There are no users on this location.

        <app-pan-button icon="add" color="primary" fill="outline" (click)="addUserModal.show(); tryLoadModalUsers(userFilter())"
          >Add one</app-pan-button
        >
      </app-pan-blank-slate>
    } @else if (selectedLocation() && visibleUsers().length === 0 && hasLoadedUserLocations()) {
      <app-pan-blank-slate>
        No users match your query.

        <app-pan-button icon="add" color="primary" fill="outline" (click)="addUserModal.show(); tryLoadModalUsers(userFilter())"
          >Add one</app-pan-button
        >
      </app-pan-blank-slate>
    } @else {
      <cdk-virtual-scroll-viewport [itemSize]="75" class="h-[560px]">
        <div *cdkVirtualFor="let item of visibleUsers()" class="h-[75px] p-3 flex items-center border-b-2 flex-row">
          <div class="flex-1 flex flex-col">
            <div>{{ item.name }}</div>
            <div class="italic text-sm">{{ item.email }}</div>
            <div class="italic text-xs">
              Last Accessed: {{ item.last_case_accessed_at ? (item.last_case_accessed_at | date: "mediumDate") : "Never" }}
            </div>
          </div>

          <div class="flex-1 mr-2">
            <ng-select
              appendTo="body"
              data-testid="location-roles-selector"
              placeholder="Select roles..."
              [bindValue]="'id'"
              [bindLabel]="'name'"
              [virtualScroll]="true"
              [items]="selectableRoles()"
              [(ngModel)]="item.roles.location[selectedLocation().id]"
              [multiple]="true"
              [clearable]="false"
              (add)="addUserRole(item, $event)"
              (remove)="removeUserRole(item, $event)"
            >
              <ng-template ng-label-tmp let-item="item" let-clear="clear">
                <span class="ng-value-label">{{ item.name }}</span>

                @if (
                  userLogin.canGrantLocationPermission(item.id, selectedLocation().branch_id.toString(), selectedLocation().id.toString())
                ) {
                  <span class="ng-value-icon right" (click)="clear(item)" aria-hidden="true">×</span>
                }
              </ng-template>
            </ng-select>
          </div>

          <app-pan-button color="danger" icon="delete" [swal]="removePermissionsSwal"></app-pan-button>

          <swal
            #removePermissionsSwal
            title="Remove User Permissions?"
            text="Are you sure you want to remove permissions from {{ item.name || item.email }}?"
            icon="warning"
            confirmButtonText="Yes, Remove"
            [showCancelButton]="true"
            [focusCancel]="true"
            (confirm)="removeUserPermissions(item)"
          >
          </swal>
        </div>
      </cdk-virtual-scroll-viewport>
    }
  </div>
</div>

<app-pan-modal #addUserModal (modalClose)="clearInputs()">
  <div header>Add User</div>

  <div body class="flex flex-col justify-center items-center gap-2">
    <ng-select
      class="w-full"
      placeholder="Select user..."
      [typeahead]="modalTypeaheadInput$"
      [items]="modalFoundUsers$ | async"
      [loading]="modalIsSearching()"
      [bindLabel]="'name'"
      [virtualScroll]="true"
      [clearable]="false"
      [(ngModel)]="modalUser"
      [minTermLength]="2"
      typeToSearchText="Please enter 3 or more characters"
      addTagText="Add new user"
      [addTag]="addNewUser"
    >
      <ng-template ng-option-tmp let-item="item">
        @if (item.name) {
          <p>{{ item.name }} ({{ item.email }})</p>
        } @else {
          {{ item.email }}
        }
      </ng-template>

      <ng-template ng-label-tmp let-item="item">
        @if (item.name) {
          <p>{{ item.name }} ({{ item.email }})</p>
        } @else {
          {{ item.email }}
        }
      </ng-template>
    </ng-select>

    @if (showingCreateUser() && !this.modalUser()) {
      <div class="w-full">
        <h3 class="text-lg font-bold mb-4">Create New User</h3>
        <app-pan-form-control>
          <app-pan-floating-label>New User Name</app-pan-floating-label>
          <input type="text" [(ngModel)]="newUserName" panInput placeholder="First Last" />
        </app-pan-form-control>

        <app-pan-form-control>
          <app-pan-floating-label>New User Email</app-pan-floating-label>
          <input type="text" [(ngModel)]="newUserEmail" panInput placeholder="name@domain.com" />
        </app-pan-form-control>
      </div>
    }

    <ng-select
      class="w-full"
      data-testid="modal-location-roles-selector"
      placeholder="Select roles..."
      [bindValue]="'id'"
      [bindLabel]="'name'"
      [virtualScroll]="true"
      [items]="selectableRoles()"
      [(ngModel)]="modalRoles"
      [multiple]="true"
      [clearable]="false"
    >
      <ng-template ng-label-tmp let-item="item" let-clear="clear">
        <span class="ng-value-label">{{ item.name }}</span>

        @if (userLogin.canGrantBranchPermission(item.id, locationId())) {
          <span class="ng-value-icon right" (click)="clear(item)" aria-hidden="true">×</span>
        }
      </ng-template>
    </ng-select>
  </div>

  <div footer>
    <button panButton color="primary" (click)="submitModal()" [disabled]="!canSubmitModal()">Add User</button>
  </div>
</app-pan-modal>
